import { ChangeDetectorRef, Component, Inject, TemplateRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AssetDataPoint, DataPoint } from '@services/datapoints-data.service';
import { IOrganisationsHierarchy, OrganisationsHierarchy } from '@services/organisation-data.service';
import { Subject, takeUntil, distinctUntilChanged, filter, finalize } from 'rxjs';

/**
 * EditDashboardPanelDialogData
 */
export class EditDashboardPanelDialogData {
	panelData: any;
	panelInfo: any;
	template: TemplateRef<any>;
	templateInfo: any;
	datapoints?: DataPoint[]
}

@Component({
	selector: 'app-edit-dashboard-panel',
	templateUrl: './edit-dashboard-panel.component.html',
	styleUrls: ['./edit-dashboard-panel.component.scss']
})
export class EditDashboardPanelComponent {

	/** accountGUID */
	public accountGUID: string;

	/** organisationUID */
	public organisationUID: string;

	/** Dialog Form */
	dialogForm: FormGroup;

	/** panelData */
	public panelData: any;

	/** panelInfo */
	public panelInfo: any;

	/** template */
	public template: TemplateRef<any>;

	/** showRanges */
	public showRanges: boolean = false;

	/** defaultRangeColours */
	public defaultRangeColours: string[] = [
		'#008000', '#FFA500', '#FF0000'
	];

	/** testingValue */
	public testingValue: number = 10;

	/** sliderMin */
	public sliderMin: number = 0;

	/** sliderMax */
	public sliderMax: number = 100;

	/** numberOfMetrics */
	public numberOfMetrics: number = 0;

	/** templateInfo */
	public templateInfo: any;

	/** metricFields */
	public metricFields: Object = { text: "text", value: "value" };

	/** selectedDatapoints */
	public selectedDatapoints: DataPoint[] = [];

	/** datapoints */
	public datapoints: DataPoint[];

	/** notValid */
	public notValid: boolean = false;

	/** dpFields */
	public dpFields: Object = { text: "DPTitle", value: "DataPointID" };

	/** showDatapoints */
	public showDatapoints: boolean = false;

	/** selectedDpIDs */
	public selectedDpIDs: number[] = [];

	/** defaultColorHexValue */
	public defaultColorHexValues: { name: string, hex: string }[] = [
		{ name: "green", hex: "#008000" },
		{ name: "orange", hex: "#FFA500" },
		{ name: "red", hex: "#FF0000" }
	];

	/**
	 * Constructor
	 * @param fb
	 * @param dialogRef
	 * @param data
	 * @param globalFunctions 
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<EditDashboardPanelComponent>,
		@Inject(MAT_DIALOG_DATA) public data: EditDashboardPanelDialogData,
		public globalFunctions: GlobalFunctions,
		private assetsDataService: AssetsDataService,
		private cdr: ChangeDetectorRef
	) {
		this.panelData = this.data.panelData;
		this.panelInfo = this.data.panelInfo;
		this.template = this.data.template;
		this.templateInfo = this.data.templateInfo;
		if ([3,10].includes(this.panelInfo.templateID) && this.panelInfo.isHierarchy) {
			this.showDatapoints = true;
		}
		if (this.panelData.datapoints) {
			this.selectedDatapoints = this.panelData.datapoints;
			this.selectedDpIDs = this.selectedDatapoints.map((x: DataPoint) => x.DataPointID);
			if (this.panelInfo.isHierarchy && this.panelData.nodeData) {
				this.getDatapointsForHierarchyNode(this.panelData.nodeData);
			}
		}
		this.numberOfMetrics = this.panelData.metricData.metricChoices.length;
		if (this.panelData.ranges) {
			this.showRanges = true;
			this.getSliderMaxMin();
		}

		this.dialogForm = this.fb.group({
		});
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	/**
	 * onDialogClose
	 * @param event event 
	 */
	onDialogClose() {
		if (this.showDatapoints) {
			this.panelData.datapoints = this.selectedDatapoints;
		}
		this.dialogRef.close({ panelData: this.panelData, panelInfo: this.panelInfo });
	}

	/**
	 * checkboxChange
	 * @param event event 
	 * @param range range 
	 * @param index index 
	 */
	checkboxChange(event: any, range: any, index: number) {
		range.createsBorder[index] = event.checked;
	}

	/**
	 * colourPickerChange
	 * @param event event 
	 * @param range range 
	 * @param index index  
	 */
	colourPickerChange(event: any, range: any, index: number) {
		range.color[index] = event.value;
	}

	/**
	 * getSliderMaxMin
	 */
	getSliderMaxMin() {
		let min: number = Infinity;
		this.panelData.ranges.map((x: any) => x.min).forEach((x: number[]) => x.forEach((y: number) => min = Math.min(y, min)));
		let max: number = 0;
		this.panelData.ranges.map((x: any) => x.max).forEach((x: number[]) => x.forEach((y: number) => max = Math.max(y, max)));
		this.sliderMin = min != Infinity ? min : 0;
		this.sliderMax = max > this.sliderMin ? max : this.sliderMin + 1;
	}

	/**
	 * metricSelected
	 * @param event event 
	 * @param idx idx 
	 */
	metricSelected(event: any, idx: number) {
		this.panelData.metricData.metricChoices[idx] = event.value;
	}

	/**
	 * numberOfMetricsChange
	 * @param event event 
	 */
	numberOfMetricsChange(event: any) {
		this.panelData.metricData.metricChoices = this.panelData.metricData.metricChoices.slice(0, event.value);
	}

	/**
	 * datapointChoiceChange
	 * @param event event 
	 */
	datapointChoiceChange(event: any) {
		this.selectedDatapoints = event.value.map((x: number) => this.datapoints.find((y: DataPoint) => y.DataPointID == x));
		if(this.panelData.ranges[0].min.length < event.value.length) {
			this.panelData.ranges.forEach((x: any) => {
				x.min.push(x.min[0]);
				x.max.push(x.max[0]);
				x.name.push(x.name[0]);
			});
		}
		this.panelData.datapoints = this.selectedDatapoints;
		this.selectedDpIDs = this.selectedDatapoints.map((x: DataPoint) => x.DataPointID);
		this.notValid = false;
		for (let i: number = 0; i < this.selectedDatapoints.length; i++) {
			if (this.selectedDatapoints[i].SI != this.selectedDatapoints[0].SI) {
				this.notValid = true;
			}
		}
	}

	/**
	 * getDatapointsForHierarchyNode
	 */
	async getDatapointsForHierarchyNode(node: IOrganisationsHierarchy) {
		let datapoints: DataPoint[] = [];
		const assets = this.getAssetsFromHierarchyTreeNode(node);
		for (let i: number = 0; i < assets.length; i++) {
			const assetDPs: AssetDataPoint[] = await this.getAssetDataPoints(assets[i]);
			datapoints.push(...assetDPs);
		}
		this.datapoints = datapoints;
	}

	/**
	 * getAssetsFromHierarchyTreeNode
	 * @param treeNode treeNode 
	 */
	getAssetsFromHierarchyTreeNode(treeNode: OrganisationsHierarchy): LevelAsset[] {
		let result: LevelAsset[] = [];
		const flattenedLocations = this.flattenSubLocations([treeNode], true);
		flattenedLocations.forEach((x) => {
			if (this.isParsableAsNumber(x.Uid)) {
				result.push(x)
			}
		})
		return result;
	}

	/**
	 * Check if string can be parsed as int
	 * @param str
	 * @returns
	 */
	isParsableAsNumber(str: any): boolean {
		const parsed = Number(str);
		return !isNaN(parsed);
	}

	/**
	 * 	getAssetDataPoints
	 * @param asset asset 
	 */
	getAssetDataPoints(asset: LevelAsset): Promise<AssetDataPoint[]> {
		return new Promise<AssetDataPoint[]>((resolve, reject) => {
			const fetchDataSubject = new Subject<void>();

			this.assetsDataService.getAssetDataPoints$(this.accountGUID, this.organisationUID, asset.AssetID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((datapoints) => !!datapoints),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (datapoints) => {
					resolve(datapoints);
				},
				error: (error) => {
					reject();
				},
			});
		});
	}

	/**
	 * flattenSubLocations
	 * @param hierarchy hierarchy 
	 * @returns 
	 */
	flattenSubLocations(hierarchy: OrganisationsHierarchy[], includeTopLevel: boolean = false): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		if (includeTopLevel) {
			result.push(...hierarchy);
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.SubChild && item.SubChild.length > 0) {
				result = result.concat(item.SubChild);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenSubLocations(item.SubChild));
		}

		return result;
	}

	/**
	 * respondToBorderChange
	 * @param div div 
	 * @param colour colour 
	 */
	respondToBorderChange(div: HTMLElement, colour: any) {
		if (colour == "") {
			div.style.border = "";
			div.style.backgroundColor = "inherit";
		} else {
			div.style.border = `3px solid ${colour}`;
			div.style.borderRadius = 'var(--default-dashboard-border-radius)';
			let hex: string = "";
			if (!colour.includes("#")) {
				hex = this.getDefaultColorHexValue(colour);
			} else {
				hex = colour;
			}
			let rgb = this.convertHexToRGB(hex);
			div.style.backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25)`;
		}
	}

	/**
	 * convertHexToRGB
	 * @param hex hex 
	 */
	convertHexToRGB(hex: string): { r: number, g: number, b: number } {
		var bigint = parseInt(hex.slice(1, 7), 16);
		var r = (bigint >> 16) & 255;
		var g = (bigint >> 8) & 255;
		var b = bigint & 255;

		return { r: r, g: g, b: b };
	}

	/**
	 * getDefaultColorHexValue
	 * @param color color 
	 */
	getDefaultColorHexValue(color: string): string {
		let result: string = "";
		const _hex = this.defaultColorHexValues.find((x) => x.name == color)?.hex;
		if (_hex) result = _hex;
		return result;
	}
}
